import React, { useState, useEffect, useCallback } from 'react';
import { StageProps } from '../types';
import { LocalStorageManager } from '../utils/localStorage';

const presetSchemes = [
  {
    name: 'Formula 1 Style',
    description: '25, 18, 15, 12, 10, 8, 6, 4, 2, 1 points for top 10',
    scheme: '1st: 25, 2nd: 18, 3rd: 15, 4th: 12, 5th: 10, 6th: 8, 7th: 6, 8th: 4, 9th: 2, 10th: 1'
  },
  {
    name: 'NASCAR Style',
    description: 'Decreasing points from 40 for 1st place',
    scheme: '1st: 40, 2nd: 35, 3rd: 34, 4th: 33, 5th: 32, then decrease by 1 for each position'
  },
  {
    name: 'Simple Descending',
    description: '100 points for 1st, decreasing by 5',
    scheme: '1st: 100, 2nd: 95, 3rd: 90, 4th: 85, 5th: 80, then decrease by 5 for each position'
  },
  {
    name: 'Participation Focus',
    description: 'High base points with smaller gaps',
    scheme: '1st: 50, 2nd: 48, 3rd: 46, 4th: 44, 5th: 42, then decrease by 2 for each position, minimum 10 points'
  }
];

const PointsConfiguration: React.FC<StageProps> = ({ appData, setAppData, onNext, onPrev }) => {
  const [pointsType, setPointsType] = useState<'preset' | 'custom' | 'description'>('preset');
  const [customScheme, setCustomScheme] = useState('');
  const [description, setDescription] = useState('');
  const [raceName, setRaceName] = useState('');
  const [recentSchemes, setRecentSchemes] = useState<any[]>([]);
  const [selectedPreset, setSelectedPreset] = useState(presetSchemes[0]);
  const [hasManuallyOverridden, setHasManuallyOverridden] = useState(false);

  const isSubsequentRace = appData.workflowType === 'subsequent-race';
  const isUsingSeriesScheme = isSubsequentRace && appData.currentSeriesMetadata?.pointsScheme && !hasManuallyOverridden;

  // Helper function to detect points scheme type based on content
  const detectSchemeType = useCallback((scheme: string): 'preset' | 'custom' | 'description' => {
    // Check if it matches any preset scheme
    const matchingPreset = presetSchemes.find(p => p.scheme === scheme);
    if (matchingPreset) {
      return 'preset';
    }
    
    // Check if it looks like a structured custom scheme (contains colons and numbers)
    const customPattern = /\d+(st|nd|rd|th)?\s*:\s*\d+/;
    if (customPattern.test(scheme)) {
      return 'custom';
    }
    
    // Otherwise assume it's natural language description
    return 'description';
  }, []);

  // Helper function to populate scheme based on text and type
  const populateScheme = useCallback((schemeText: string, schemeType?: string) => {
    console.log('populateScheme called with:', { schemeText, schemeType });
    
    // Map 'natural-language' from localStorage to 'description' used in component
    let detectedType: 'preset' | 'custom' | 'description';
    
    if (schemeType === 'natural-language') {
      detectedType = 'description';
    } else if (schemeType === 'preset' || schemeType === 'custom' || schemeType === 'description') {
      detectedType = schemeType;
    } else {
      detectedType = detectSchemeType(schemeText);
    }
    
    console.log('detected type:', detectedType);
    
    setPointsType(detectedType);
    
    switch (detectedType) {
      case 'custom':
        console.log('Setting custom scheme:', schemeText);
        setCustomScheme(schemeText);
        break;
      case 'description':
        console.log('Setting description:', schemeText);
        setDescription(schemeText);
        break;
      case 'preset':
        const matchingPreset = presetSchemes.find(p => p.scheme === schemeText);
        console.log('Looking for matching preset for:', schemeText);
        console.log('Found matching preset:', matchingPreset);
        if (matchingPreset) {
          setSelectedPreset(matchingPreset);
        }
        break;
    }
  }, [detectSchemeType]);

  useEffect(() => {
    // Load recent schemes for display
    const recent = LocalStorageManager.getRecentPointsSchemes();
    setRecentSchemes(recent);
    
    // Priority 1: Use existing series points scheme (for subsequent races)
    if (isSubsequentRace && appData.currentSeriesMetadata?.pointsScheme && !appData.pointsScheme) {
      console.log('Pre-populating with existing series points scheme:', appData.currentSeriesMetadata.pointsScheme);
      populateScheme(appData.currentSeriesMetadata.pointsScheme);
    }
    // Priority 2: Use AppData points scheme if already set
    else if (appData.pointsScheme) {
      console.log('Using AppData points scheme:', appData.pointsScheme);
      populateScheme(appData.pointsScheme);
    }
    // Priority 3: Fall back to last used scheme from localStorage
    else {
      const lastUsed = LocalStorageManager.getLastUsedPointsScheme();
      if (lastUsed) {
        console.log('Using last used points scheme from localStorage');
        populateScheme(lastUsed.text, lastUsed.type);
      }
    }

    // For subsequent races, we need to set up the race name since there's no review step
    if (isSubsequentRace && appData.newRaceData) {
      if (appData.newRaceData.raceName) {
        setRaceName(appData.newRaceData.raceName);
      } else if (appData.newRaceData.fileName) {
        // Generate smart default race name
        const cleanName = appData.newRaceData.fileName
          .replace(/\.[^/.]+$/, '') // Remove file extension
          .replace(/[-_]/g, ' ') // Replace dashes and underscores with spaces
          .replace(/\b\w/g, l => l.toUpperCase()); // Title case - let user decide on length
        setRaceName(cleanName);
      }
    }
  }, [appData.newRaceData, appData.pointsScheme, appData.currentSeriesMetadata, isSubsequentRace, populateScheme]);

  const handleNext = () => {
    let finalScheme = '';
    let schemeType: 'preset' | 'custom' | 'natural-language' = 'preset';
    
    // If using series scheme, use that
    if (isUsingSeriesScheme && appData.currentSeriesMetadata?.pointsScheme) {
      finalScheme = appData.currentSeriesMetadata.pointsScheme;
      schemeType = 'natural-language'; // Assume series schemes are natural language
    } else {
      // Otherwise use the manually selected scheme
      switch (pointsType) {
        case 'preset':
          finalScheme = selectedPreset.scheme;
          schemeType = 'preset';
          break;
        case 'custom':
          finalScheme = customScheme;
          schemeType = 'custom';
          break;
        case 'description':
          finalScheme = description;
          schemeType = 'natural-language';
          break;
      }
    }

    // Save the scheme to localStorage
    LocalStorageManager.savePointsScheme(finalScheme, schemeType);

    // Update race name for subsequent races
    let updatedAppData = {
      ...appData,
      pointsScheme: finalScheme
    };

    if (isSubsequentRace && raceName.trim() && appData.newRaceData) {
      updatedAppData = {
        ...updatedAppData,
        newRaceData: {
          ...appData.newRaceData,
          raceName: raceName.trim()
        }
      };
    }

    setAppData(updatedAppData);
    onNext?.();
  };

  const loadRecentScheme = (scheme: any) => {
    console.log('loadRecentScheme called with:', scheme);
    setHasManuallyOverridden(true); // Mark that user has manually overridden series scheme
    populateScheme(scheme.text, scheme.type);
  };

  // Function to reset back to series scheme
  const resetToSeriesScheme = () => {
    setHasManuallyOverridden(false);
    setCustomScheme('');
    setDescription('');
    // Reset to default preset
    setSelectedPreset(presetSchemes[0]);
    setPointsType('preset');
  };

  const canProceed = () => {
    // Check if race name is provided for subsequent races
    if (isSubsequentRace && !raceName.trim()) {
      return false;
    }

    // If using series scheme and haven't manually overridden, allow proceeding
    if (isUsingSeriesScheme) {
      return true;
    }

    // Otherwise check based on current points type
    switch (pointsType) {
      case 'preset':
        return true;
      case 'custom':
        return customScheme.trim().length > 0;
      case 'description':
        return description.trim().length > 0;
      default:
        return false;
    }
  };

  return (
    <div className="stage-container">
      <div className="stage-header">
        <h2>Configure Points Scheme</h2>
        <p>Define how points should be awarded based on race positions. Your scheme will be applied to all race results.</p>
      </div>

      {/* Race Name Input for Subsequent Races - MOVED TO TOP */}
      {isSubsequentRace && (
        <div className="form-group">
          <label htmlFor="raceName" style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
            🏁 Race Name <span style={{ color: '#718096', fontWeight: 'normal' }}>(will appear as column header in series results)</span>
          </label>
          <input
            id="raceName"
            type="text"
            className={`form-control ${raceName.length > 15 ? 'warning-border' : ''}`}
            value={raceName}
            onChange={(e) => setRaceName(e.target.value)}
            placeholder="Enter a short race name..."
            maxLength={30}
            style={{ 
              fontSize: '1.1rem', 
              fontWeight: '600',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              border: raceName.length > 15 ? '2px solid #f56565' : '2px solid #e2e8f0'
            }}
          />
          {raceName.length > 15 ? (
            <div style={{ 
              color: '#c53030', 
              marginTop: '0.5rem', 
              display: 'block',
              fontWeight: '600',
              backgroundColor: '#fed7d7',
              padding: '0.75rem',
              borderRadius: '6px',
              border: '1px solid #feb2b2'
            }}>
              ⚠️ <strong>Name too long!</strong> Consider shortening by {raceName.length - 15} characters for better column headers.
            </div>
          ) : (
            <small style={{ color: '#718096', marginTop: '0.5rem', display: 'block' }}>
              💡 Keep it short for better column headers (e.g., "Race 2", "Summer XC", "Finals")
            </small>
          )}
        </div>
      )}

      {/* Recent Schemes Section */}
      {recentSchemes.length > 0 && (
        <div className="form-group">
          <label>🕒 Recently Used Schemes</label>
          <div style={{ display: 'grid', gap: '0.5rem', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
            {recentSchemes.slice(0, 3).map((scheme, index) => (
              <div
                key={index}
                className="file-info"
                style={{ 
                  cursor: 'pointer', 
                  border: '1px solid #e2e8f0',
                  background: '#f7fafc',
                  fontSize: '0.9rem',
                  padding: '0.75rem'
                }}
                onClick={() => loadRecentScheme(scheme)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                  <strong>{scheme.type === 'natural-language' ? 'Natural Language' : scheme.type === 'custom' ? 'Custom' : 'Preset'}</strong>
                  <small style={{ color: '#718096' }}>
                    {new Date(scheme.timestamp).toLocaleDateString()}
                  </small>
                </div>
                <div style={{ 
                  color: '#4a5568', 
                  fontSize: '0.8rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {scheme.text.substring(0, 60)}{scheme.text.length > 60 ? '...' : ''}
                </div>
              </div>
            ))}
          </div>
          <small style={{ color: '#718096', marginTop: '0.5rem', display: 'block' }}>
            💡 Click any recent scheme to quickly load it
          </small>
        </div>
      )}

      {/* Series Points Scheme Notice */}
      {isUsingSeriesScheme && appData.currentSeriesMetadata && (
        <div className="info-box" style={{ backgroundColor: '#e6fffa', border: '1px solid #38b2ac', marginBottom: '1.5rem' }}>
          <h4>📋 Using Series Points Scheme</h4>
          <p>
            The points scheme has been automatically loaded from your existing series: <strong>{appData.currentSeriesMetadata?.name}</strong>
          </p>
          <div style={{ 
            backgroundColor: 'rgba(56, 178, 172, 0.1)', 
            padding: '0.75rem', 
            borderRadius: '6px', 
            marginTop: '0.75rem',
            border: '1px solid rgba(56, 178, 172, 0.2)'
          }}>
            <strong>Current scheme:</strong><br/>
            <code style={{ 
              fontSize: '0.9rem', 
              color: '#2c7a7b',
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap'
            }}>
              {appData.currentSeriesMetadata?.pointsScheme}
            </code>
          </div>
          <small style={{ color: '#2c7a7b' }}>
            💡 You can override this by selecting a different scheme type below or clicking a recent scheme above.
          </small>
        </div>
      )}

      {/* Show option to return to series scheme if user has overridden */}
      {isSubsequentRace && appData.currentSeriesMetadata?.pointsScheme && hasManuallyOverridden && (
        <div className="info-box" style={{ backgroundColor: '#fef5e7', border: '1px solid #d69e2e', marginBottom: '1.5rem' }}>
          <h4>⚠️ Using Manual Points Scheme</h4>
          <p>You've overridden the series points scheme. You can return to using the original series scheme if needed.</p>
          <button 
            className="btn btn-secondary"
            onClick={resetToSeriesScheme}
            style={{ marginTop: '0.5rem' }}
          >
            ↩️ Return to Series Scheme
          </button>
        </div>
      )}

      {/* Points Scheme Type Selection - Always show, but none selected when using series scheme */}
      <div className="form-group">
        <label>Points Scheme Type</label>
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="radio"
              value="preset"
              checked={!isUsingSeriesScheme && pointsType === 'preset'}
              onChange={(e) => {
                setPointsType(e.target.value as any);
                setHasManuallyOverridden(true);
              }}
            />
            Preset Schemes
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="radio"
              value="custom"
              checked={!isUsingSeriesScheme && pointsType === 'custom'}
              onChange={(e) => {
                setPointsType(e.target.value as any);
                setHasManuallyOverridden(true);
              }}
            />
            Custom Formula
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="radio"
              value="description"
              checked={!isUsingSeriesScheme && pointsType === 'description'}
              onChange={(e) => {
                setPointsType(e.target.value as any);
                setHasManuallyOverridden(true);
              }}
            />
            Natural Language
          </label>
        </div>
      </div>

      {/* Preset Schemes - Only show if preset is selected and not using series scheme */}
      {!isUsingSeriesScheme && pointsType === 'preset' && (
        <div className="form-group">
          <label>Select Preset Scheme</label>
          <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            {presetSchemes.map((preset, index) => (
              <div
                key={index}
                className={`file-info ${selectedPreset === preset ? 'selected' : ''}`}
                style={{ 
                  cursor: 'pointer', 
                  border: selectedPreset === preset ? '2px solid #667eea' : '1px solid #e2e8f0',
                  background: selectedPreset === preset ? 'rgba(102, 126, 234, 0.05)' : '#f7fafc'
                }}
                onClick={() => setSelectedPreset(preset)}
              >
                <h4>{preset.name}</h4>
                <p style={{ marginBottom: '0.5rem' }}>{preset.description}</p>
                <small style={{ color: '#718096', fontSize: '0.8rem' }}>{preset.scheme}</small>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Custom Formula - Only show if custom is selected and not using series scheme */}
      {!isUsingSeriesScheme && pointsType === 'custom' && (
        <div className="form-group">
          <label>Custom Points Formula</label>
          <textarea
            className="form-control textarea"
            placeholder="Enter your custom points scheme, e.g.:
1st place: 100 points
2nd place: 80 points  
3rd place: 70 points
4th place: 65 points
5th place: 60 points
Then decrease by 5 points for each position"
            value={customScheme}
            onChange={(e) => {
              setCustomScheme(e.target.value);
              setHasManuallyOverridden(true);
            }}
          />
          <small style={{ color: '#718096', marginTop: '0.5rem', display: 'block' }}>
            Be specific about the points for each position or provide a clear pattern.
          </small>
        </div>
      )}

      {/* Natural Language Description - Only show if description is selected and not using series scheme */}
      {!isUsingSeriesScheme && pointsType === 'description' && (
        <div className="form-group">
          <label>Describe Your Points System</label>
          <textarea
            className="form-control textarea"
            placeholder="Describe your points system in plain English, e.g.:
'Give 100 points for first place, 80 for second, 70 for third, then decrease by 5 points for each position after that. Everyone who finishes gets at least 10 points.'

or

'Use a NASCAR-style system where first place gets 40 points, second gets 35, third gets 34, and then decrease by 1 point for each position.'

or

'Award points based on the inverse of finishing position multiplied by 10, so 1st gets 10 points, 2nd gets 5 points, 3rd gets 3.33 points, etc.'"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              setHasManuallyOverridden(true);
            }}
          />
          <small style={{ color: '#718096', marginTop: '0.5rem', display: 'block' }}>
            Your description will be processed and converted into the appropriate points scheme.
          </small>
        </div>
      )}

      {appData.newRaceData && (
        <div className="file-info">
          <h4>📊 Race Preview</h4>
          <p>
            <strong>{appData.newRaceData.raceName}</strong><br />
            {appData.newRaceData.participants.length} participants will receive points based on your scheme
          </p>
        </div>
      )}

      <div className="stage-actions">
        <button className="btn btn-secondary" onClick={onPrev}>
          ← Back
        </button>
        <button 
          className="btn btn-primary" 
          onClick={handleNext}
          disabled={!canProceed()}
        >
          Next: Apply Points →
        </button>
      </div>
    </div>
  );
};

export default PointsConfiguration; 